import {supabase} from '../lib/supabase'

export const authService = {
  // Sign up with email and password
  async signUp(email, password, userData) {
    try {
      const {data, error} = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: userData
        }
      })

      if (error) throw error

      // Create profile after successful signup
      if (data.user) {
        await this.createProfile(data.user.id, userData)
      }

      return {user: data.user, error: null}
    } catch (error) {
      return {user: null, error: error.message}
    }
  },

  // Sign in with email and password
  async signIn(email, password) {
    try {
      const {data, error} = await supabase.auth.signInWithPassword({
        email,
        password
      })

      if (error) throw error

      // Get user profile
      const profile = await this.getProfile(data.user.id)

      return {user: data.user, profile: profile.data, error: null}
    } catch (error) {
      return {user: null, profile: null, error: error.message}
    }
  },

  // Sign out
  async signOut() {
    try {
      const {error} = await supabase.auth.signOut()
      if (error) throw error
      return {error: null}
    } catch (error) {
      return {error: error.message}
    }
  },

  // Get current user
  async getCurrentUser() {
    try {
      const {data: {user}, error} = await supabase.auth.getUser()
      if (error) throw error

      if (user) {
        const profile = await this.getProfile(user.id)
        return {user, profile: profile.data, error: null}
      }

      return {user: null, profile: null, error: null}
    } catch (error) {
      return {user: null, profile: null, error: error.message}
    }
  },

  // Create user profile
  async createProfile(userId, userData) {
    try {
      // First check if profile already exists
      const {data: existingProfile} = await supabase
        .from('profiles_clomapper')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle()

      if (existingProfile) {
        // Profile already exists, update it instead
        return await this.updateProfile(userId, userData)
      }

      // Create new profile
      const {data, error} = await supabase
        .from('profiles_clomapper')
        .insert([
          {
            user_id: userId,
            name: userData.name || '',
            email: userData.email || '',
            faculty: userData.faculty || '',
            department: userData.department || '',
            position: userData.position || '',
            phone: userData.phone || '',
            office: userData.office || '',
            profile_picture: userData.profile_picture || null
          }
        ])
        .select()
        .single()

      if (error) throw error

      return {data, error: null}
    } catch (error) {
      return {data: null, error: error.message}
    }
  },

  // Get user profile
  async getProfile(userId) {
    try {
      const {data, error} = await supabase
        .from('profiles_clomapper')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle()

      if (error) throw error

      // If no profile exists, create a default one
      if (!data) {
        const {data: user} = await supabase.auth.getUser()
        const defaultProfile = {
          name: user.user?.user_metadata?.name || '',
          email: user.user?.email || '',
          faculty: '',
          department: '',
          position: '',
          phone: '',
          office: '',
          profile_picture: null
        }
        const createResult = await this.createProfile(userId, defaultProfile)
        return createResult
      }

      return {data, error: null}
    } catch (error) {
      return {data: null, error: error.message}
    }
  },

  // Update user profile
  async updateProfile(userId, updates) {
    try {
      // First check if profile exists
      const {data: existingProfile} = await supabase
        .from('profiles_clomapper')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle()

      if (!existingProfile) {
        // Profile doesn't exist, create it
        return await this.createProfile(userId, updates)
      }

      // Update existing profile
      const {data, error} = await supabase
        .from('profiles_clomapper')
        .update(updates)
        .eq('user_id', userId)
        .select()
        .single()

      if (error) throw error

      return {data, error: null}
    } catch (error) {
      return {data: null, error: error.message}
    }
  },

  // Listen to auth changes
  onAuthStateChange(callback) {
    return supabase.auth.onAuthStateChange(callback)
  }
}